import React, { useCallback, useEffect, useState } from "react";
import { Box, Flex } from "theme-ui";
import { Button, Heading, Icon, Card } from "@loanpal/lumos__core";
import {
  createPaymentCustomer,
  createPaymentInvoice,
  createPaymentTransaction,
  resolveInvoiceLink,
  sendPaymentInvoice,
  verifyPaymentLink,
} from "../../utils/api";
import {
  numberToMoney,
  numberToMoney2,
  moneyStringToNumber,
  phone as formatPhoneDisplay,
  phoneDigits,
  omitNamePlusSuffix,
} from "../../utils/format";
import { checkAndResolveSubmittingUserWithRetry } from "../../utils/submitting-user";
import { fetchPaymentsLocalActorOverride } from "../../utils/payments-local";
import { CUSTOMER_SUPPORT_PHONE } from "../../utils/glodbals";
import StreetAddressAutocomplete from "./StreetAddressAutocomplete";

const STEPS = ["Start new payment", "Payment Details", "Create Payment Link"];

/**
 * Resolve which Create Payment Link share methods to show.
 * Source: SharedApp.payments.shareMethods (same shape on Requests for admin later).
 * Missing config → email + online on, SMS off (until org enables it).
 */
function getInvoiceShareMethodFlags(payments) {
  const sm = payments?.shareMethods;
  if (!sm || typeof sm !== "object") {
    return { email: true, sms: false, online: true };
  }
  return {
    email: sm.email !== false,
    sms: sm.sms === true,
    online: sm.online !== false,
  };
}

function firstEnabledShareMethod(flags) {
  if (flags.email) return "email";
  if (flags.online) return "manual";
  if (flags.sms) return "sms";
  return "email";
}

/** Pay-only visual tokens — aligned with Financing+ / GoodLeap Passion–Grape. */
const PAY = {
  radius: "12px",
  radiusSm: "10px",
  fieldHeight: 40,
  pageBg: "#F6F5F4",
  fieldBg: "#FFFEFC",
  cardShadow:
    "0 1px 2px rgba(16, 24, 40, 0.04), 0 8px 24px rgba(16, 24, 40, 0.06)",
  /** GoodLeap Passion / Grape (same as Financing+) */
  accent: "#472BA4",
  accentHover: "#200F51",
  accentMuted: "#D1C1FF",
  accentSoft: "rgba(71, 43, 164, 0.1)",
  accentWarm: "#FF8300",
  focusRing: "0 0 0 3px rgba(71, 43, 164, 0.22)",
};

const fieldSx = {
  width: "100%",
  height: PAY.fieldHeight,
  px: 3,
  border: "1px solid",
  borderColor: "neutral.l3",
  borderRadius: PAY.radiusSm,
  fontSize: 2,
  boxSizing: "border-box",
  bg: PAY.fieldBg,
  color: "neutral.d1",
  transition:
    "border-color 150ms ease, box-shadow 150ms ease, background 150ms ease",
  "&:hover": {
    borderColor: "neutral.l2",
  },
  "&:focus": {
    outline: "none",
    borderColor: PAY.accent,
    bg: "white",
    boxShadow: PAY.focusRing,
  },
};

const labelSx = {
  display: "block",
  mb: "6px",
  fontSize: 2,
  fontWeight: 500,
  color: "neutral.d1",
  letterSpacing: "0.01em",
};

const primaryButtonSx = {
  borderRadius: PAY.radiusSm,
  height: 40,
  px: 3,
  fontWeight: 600,
  bg: PAY.accent,
  color: "white",
  border: "1px solid",
  borderColor: PAY.accent,
  "&:hover": {
    bg: PAY.accentHover,
    borderColor: PAY.accentHover,
    color: "white",
  },
  "&:disabled": {
    opacity: 0.5,
    cursor: "default",
  },
};

const secondaryButtonSx = {
  borderRadius: PAY.radiusSm,
  height: 40,
  px: 3,
  fontWeight: 600,
  color: PAY.accent,
  borderColor: PAY.accent,
  bg: "transparent",
  "&:hover": {
    bg: PAY.accentSoft,
    color: PAY.accentHover,
    borderColor: PAY.accentHover,
  },
};

const cardSx = {
  border: "1px solid",
  borderColor: "neutral.l3",
  borderRadius: PAY.radius,
  p: [3, 4],
  mb: 3,
  bg: "white",
  boxShadow: PAY.cardShadow,
};

function ProgressStepper({ activeStep }) {
  return (
    <Flex
      sx={{
        alignItems: "center",
        width: "100%",
        mb: 4,
        px: [3, 4],
        py: 3,
        bg: "white",
        borderRadius: PAY.radius,
        border: "1px solid",
        borderColor: "neutral.l3",
        boxShadow: PAY.cardShadow,
        gap: 0,
        flexWrap: ["wrap", "nowrap"],
        rowGap: 3,
      }}
      aria-label="Payment steps"
    >
      {STEPS.map((label, i) => {
        const isActive = i === activeStep;
        const isComplete = i < activeStep;
        const isCurrentOrDone = isActive || isComplete;
        return (
          <React.Fragment key={label}>
            <Flex
              sx={{
                alignItems: "center",
                gap: 2,
                flex: "0 0 auto",
                minWidth: 0,
                bg: "white",
                zIndex: 1,
                px: "2px",
              }}
            >
              <Flex
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  bg: isCurrentOrDone ? PAY.accent : "neutral.l3",
                  color: "white",
                  fontSize: 0,
                  fontWeight: 700,
                  lineHeight: 1,
                  boxShadow: isActive ? PAY.focusRing : "none",
                }}
                aria-current={isActive ? "step" : undefined}
              >
                {i + 1}
              </Flex>
              <Box
                sx={{
                  fontSize: 2,
                  fontWeight: isActive ? 600 : 500,
                  color: isCurrentOrDone ? "neutral.d1" : "neutral.d2",
                  whiteSpace: "nowrap",
                  bg: "white",
                }}
              >
                {label}
              </Box>
            </Flex>
            {i < STEPS.length - 1 ? (
              <Box
                sx={{
                  flex: "1 1 24px",
                  height: "2px",
                  minWidth: 16,
                  bg: isComplete ? PAY.accentMuted : "neutral.l3",
                  mx: 3,
                  borderRadius: "999px",
                  alignSelf: "center",
                }}
                aria-hidden
              />
            ) : null}
          </React.Fragment>
        );
      })}
    </Flex>
  );
}

const backButtonSx = {
  mb: 3,
  color: PAY.accent,
  border: "1px solid",
  borderColor: PAY.accent,
  bg: "transparent",
  display: "inline-flex",
  alignItems: "center",
  gap: 2,
  px: 3,
  height: 40,
  borderRadius: PAY.radiusSm,
  fontWeight: 600,
  cursor: "pointer",
  transition: "background 150ms ease, border-color 150ms ease",
  "&:hover": {
    bg: PAY.accentSoft,
    color: PAY.accentHover,
    borderColor: PAY.accentHover,
  },
  "&:disabled": {
    opacity: 0.5,
    cursor: "default",
  },
};

function BackButton({ onClick, disabled }) {
  return (
    <Button
      variant="ghost"
      type="button"
      onClick={onClick}
      disabled={disabled}
      sx={backButtonSx}
    >
      <Box as="span" sx={{ color: PAY.accent, lineHeight: 1 }} aria-hidden>
        ←
      </Box>
      Back
    </Button>
  );
}

function StepStatusIcon({ status }) {
  if (status === "complete") {
    return (
      <Icon
        variant="check-circle"
        size="sm"
        sx={{ color: "#15803d", flexShrink: 0 }}
      />
    );
  }
  if (status === "active") {
    return (
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          border: "2px solid",
          borderColor: PAY.accent,
          overflow: "hidden",
          flexShrink: 0,
          position: "relative",
          bg: "white",
        }}
        aria-hidden
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "50%",
            height: "100%",
            bg: PAY.accent,
          }}
        />
      </Box>
    );
  }
  return (
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: "50%",
        border: "2px solid",
        borderColor: "neutral.l2",
        flexShrink: 0,
        bg: "white",
      }}
      aria-hidden
    />
  );
}

function SummaryDetailRow({ icon, children }) {
  return (
    <Flex
      sx={{
        alignItems: "flex-start",
        gap: 2,
        minHeight: 24,
        color: "neutral.d1",
        fontSize: 2,
      }}
    >
      <Box
        sx={{
          width: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          mt: "1px",
          color: "neutral.d2",
        }}
      >
        <Icon variant={icon} size="sm" />
      </Box>
      <Box sx={{ lineHeight: "20px", wordBreak: "break-word", flex: 1 }}>
        {children}
      </Box>
    </Flex>
  );
}

function SentCheckmark() {
  return (
    <Flex
      as="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        ml: 2,
        color: "#15803d",
        fontSize: 1,
        fontWeight: 700,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        verticalAlign: "middle",
      }}
    >
      <Icon variant="check-circle" size="sm" sx={{ color: "#15803d" }} />
      Sent
    </Flex>
  );
}

function NextStepBlock({ status, title, onEdit, children }) {
  const bg =
    status === "active"
      ? PAY.accentSoft
      : status === "complete"
        ? "#f3f4f6"
        : "#f9fafb";

  return (
    <Box
      sx={{
        bg,
        borderRadius: PAY.radiusSm,
        px: 3,
        py: 3,
        mb: 2,
      }}
    >
      <Flex sx={{ alignItems: "center", gap: 2, minHeight: 28 }}>
        <StepStatusIcon status={status} />
        <Box sx={{ flex: 1, fontWeight: 600, fontSize: 2, color: "neutral.d1" }}>
          {title}
        </Box>
        {status === "complete" && onEdit ? (
          <Button
            variant="ghost"
            type="button"
            aria-label={`Edit ${title}`}
            onClick={onEdit}
            sx={{
              p: 1,
              minWidth: 0,
              borderRadius: "8px",
              color: "neutral.d2",
              "&:hover": { color: "neutral.d1", bg: "rgba(0,0,0,0.04)" },
            }}
          >
            <Icon variant="edit" size="sm" />
          </Button>
        ) : null}
      </Flex>
      {children ? <Box sx={{ mt: 2, pl: "30px" }}>{children}</Box> : null}
    </Box>
  );
}

function Field({ id, label, description, children }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Box as="label" htmlFor={id} sx={labelSx}>
        {label}
      </Box>
      {description ? (
        <Box sx={{ mb: 1, fontSize: 1, color: "neutral.d2" }}>{description}</Box>
      ) : null}
      {children}
    </Box>
  );
}

const emptyCustomerForm = (overrides = {}) => ({
  email: "",
  firstName: "",
  lastName: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  postalCode: "",
  referenceId: "",
  amount: "",
  description: "",
  ...overrides,
});

function actorFromSubmittingUser(user) {
  if (!user?.email) return undefined;
  return {
    email: String(user.email).trim(),
    firstName: omitNamePlusSuffix(user.firstName) || "—",
    lastName: omitNamePlusSuffix(user.lastName) || "—",
  };
}

function parseAmount(value) {
  const n = moneyStringToNumber(value);
  if (n == null || Number.isNaN(n) || !Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100) / 100;
}

/** Format as (xxx) xxx-xxxx while typing; max 10 digits. */
function formatPhoneInput(value) {
  const digits = phoneDigits(value).slice(0, 10);
  if (!digits) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function dollarsToCents(dollars) {
  return Math.round(dollars * 100);
}

async function copyTextToClipboard(text) {
  const value = String(text || "");
  if (!value) return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // fall through to legacy copy
  }
  try {
    const el = document.createElement("textarea");
    el.value = value;
    el.setAttribute("readonly", "");
    el.style.position = "fixed";
    el.style.left = "-9999px";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}

function formatAddress(form) {
  const street = [form.line1.trim(), form.line2.trim()].filter(Boolean).join(" ");
  const cityStateZip = [
    form.city.trim(),
    [form.state.trim(), form.postalCode.trim()].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
  return [street, cityStateZip].filter(Boolean).join(" ");
}

/** All US states + DC for Project address (not limited by partner GET /states). */
const US_PROJECT_ADDRESS_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

/** Match Places short/long state names to dropdown option values (2-letter codes). */
function resolveStateCode(address) {
  const short = String(address?.state || "")
    .trim()
    .toUpperCase();
  const longName = String(address?.stateLong || "")
    .trim()
    .toUpperCase();
  if (!short && !longName) return "";

  const byCode = US_PROJECT_ADDRESS_STATES.find((s) => s.code === short);
  if (byCode) return byCode.code;
  const byName = US_PROJECT_ADDRESS_STATES.find(
    (s) =>
      s.name.toUpperCase() === longName ||
      s.name.toUpperCase() === short
  );
  if (byName) return byName.code;

  return short.length === 2 ? short : "";
}

/**
 * Multi-step payments invoice flow (ported from payments-temp).
 * Gate (paymentsEnabled) is handled by the parent pay page.
 * Actor is resolved from SharedApp.submittingUser (same verify/substitute logic as apply).
 */
export default function PaymentsFlow({
  sharedApp,
  clientId,
  elevatedToken,
  paymentsConfig,
  partnerName,
  accountOwnerEmail,
}) {
  const sharedAppId = String(sharedApp?.id || "").trim();
  const displayPartnerName =
    omitNamePlusSuffix(partnerName) ||
    String(partnerName || "").trim() ||
    "this partner";

  // Capture query params once on mount (ignore later URL bar edits without reload).
  const [queryLock] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      ref: String(params.get("ref") ?? "").trim(),
      exp: String(params.get("exp") ?? "").trim(),
      sig: String(params.get("sig") ?? "").trim(),
    };
  });

  const [lockedRef, setLockedRef] = useState("");
  const [refLockReady, setRefLockReady] = useState(!queryLock.ref);
  const [refVerifyError, setRefVerifyError] = useState("");
  /** Signed link failed verification — show partner contact error page. */
  const [refLinkInvalid, setRefLinkInvalid] = useState(false);

  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(() => emptyCustomerForm());
  const [actor, setActor] = useState(undefined);
  const [actorLoading, setActorLoading] = useState(true);
  const [actorError, setActorError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [transactionResult, setTransactionResult] = useState(null);
  const [invoiceResult, setInvoiceResult] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [shareMethod, setShareMethod] = useState("email"); // "email" | "sms" | "manual"
  const [invoiceEmail, setInvoiceEmail] = useState("");
  const [invoicePhone, setInvoicePhone] = useState("");

  const shareMethodFlags = getInvoiceShareMethodFlags(sharedApp?.payments);
  const {
    email: shareEmailEnabled,
    sms: shareSmsEnabled,
    online: shareOnlineEnabled,
  } = shareMethodFlags;

  useEffect(() => {
    const enabled =
      (shareMethod === "email" && shareEmailEnabled) ||
      (shareMethod === "sms" && shareSmsEnabled) ||
      (shareMethod === "manual" && shareOnlineEnabled);
    if (!enabled) {
      setShareMethod(
        firstEnabledShareMethod({
          email: shareEmailEnabled,
          sms: shareSmsEnabled,
          online: shareOnlineEnabled,
        })
      );
    }
  }, [
    shareMethod,
    shareEmailEnabled,
    shareSmsEnabled,
    shareOnlineEnabled,
  ]);

  useEffect(() => {
    let cancelled = false;
    async function resolveRefLock() {
      const { ref, exp, sig } = queryLock;
      if (!ref) {
        if (!cancelled) {
          setLockedRef("");
          setRefLockReady(true);
        }
        return;
      }

      // Signed link: verify server-side before locking.
      if (sig && exp) {
        if (!sharedAppId) {
          if (!cancelled) {
            setRefVerifyError("Missing payment link id for signature check.");
            setLockedRef("");
            setRefLinkInvalid(true);
            setRefLockReady(true);
          }
          return;
        }
        try {
          const result = await verifyPaymentLink({
            payLinkId: sharedAppId,
            ref,
            exp,
            sig,
          });
          if (cancelled) return;
          if (result?.valid) {
            setLockedRef(ref);
            setForm((prev) => ({ ...prev, referenceId: ref }));
            setRefVerifyError("");
            setRefLinkInvalid(false);
          } else {
            setLockedRef("");
            setRefLinkInvalid(true);
            setRefVerifyError(
              result?.error ||
                "This payment link signature is invalid or expired."
            );
          }
        } catch (err) {
          if (!cancelled) {
            setLockedRef("");
            setRefLinkInvalid(true);
            setRefVerifyError(
              err?.message || "Could not verify payment link signature."
            );
          }
        } finally {
          if (!cancelled) setRefLockReady(true);
        }
        return;
      }

      // Unsigned ?ref= — soft lock (same UX as before; not tamper-proof).
      if (!cancelled) {
        setLockedRef(ref);
        setForm((prev) => ({ ...prev, referenceId: ref }));
        setRefVerifyError("");
        setRefLockReady(true);
      }
    }
    resolveRefLock();
    return () => {
      cancelled = true;
    };
  }, [queryLock, sharedAppId]);

  const hasAddressInput = Boolean(
    form.line1.trim() ||
      form.city.trim() ||
      form.state.trim() ||
      form.postalCode.trim()
  );

  const canContinueStep0 =
    Boolean(actor) &&
    Boolean(
      form.email.trim() &&
        omitNamePlusSuffix(form.firstName) &&
        omitNamePlusSuffix(form.lastName) &&
        form.line1.trim() &&
        form.city.trim() &&
        form.state.trim() &&
        form.postalCode.trim() &&
        (lockedRef || form.referenceId.trim())
    );

  const canContinueStep1 =
    Boolean(form.description.trim()) && parseAmount(form.amount) !== null;

  const apiEnvironment =
    paymentsConfig?.apiEnvironment === "production" ? "production" : "sandbox";

  useEffect(() => {
    let cancelled = false;

    async function resolvePaymentActor() {
      setActorLoading(true);
      setActorError("");
      setActor(undefined);

      const localActor = await fetchPaymentsLocalActorOverride();
      if (cancelled) return;
      if (localActor) {
        const nextActor = actorFromSubmittingUser(localActor);
        setActor(nextActor);
        setActorLoading(false);
        console.log(
          "[payments] actor for payment endpoints (payments-local.json override)",
          nextActor
        );
        return;
      }

      const configured = sharedApp?.submittingUser;
      const configuredEmail = configured?.email;
      if (!clientId || !configuredEmail) {
        if (!cancelled) {
          setActorError("SharedApp has no submittingUser email.");
          setActorLoading(false);
          console.log(
            "[payments] actor unresolved — missing SharedApp.submittingUser.email",
            { clientId, submittingUser: configured }
          );
        }
        return;
      }

      console.log(
        "[payments] resolving actor from SharedApp.submittingUser",
        configured
      );

      try {
        const result = await checkAndResolveSubmittingUserWithRetry(
          clientId,
          configuredEmail,
          elevatedToken
        );
        if (cancelled) return;

        let sourceUser = configured;
        if (result.status === "configured") {
          sourceUser = configured;
        } else if (result.status === "substituted" && result.substitute) {
          sourceUser = result.substitute;
        } else {
          setActorError(
            result.reason ||
              "Could not verify submitting user for payment actor."
          );
          setActor(undefined);
          console.log("[payments] actor resolve failed", result);
          return;
        }

        const nextActor = actorFromSubmittingUser(sourceUser);
        setActor(nextActor);
        console.log("[payments] actor for payment endpoints", nextActor, {
          resolveStatus: result.status,
          roleName: result.roleName,
          configuredEmail,
        });
      } catch (err) {
        if (!cancelled) {
          setActorError("Failed to resolve payment actor.");
          console.log("[payments] actor resolve error", err);
        }
      } finally {
        if (!cancelled) setActorLoading(false);
      }
    }

    resolvePaymentActor();
    return () => {
      cancelled = true;
    };
  }, [sharedApp, clientId, elevatedToken]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateAmount = (raw) => {
    const digits = String(raw ?? "").replace(/[^0-9]/g, "");
    if (!digits) {
      setForm((prev) => ({ ...prev, amount: "" }));
      return;
    }
    const wholeDollars = Number(digits.slice(0, 9));
    if (!Number.isFinite(wholeDollars) || wholeDollars <= 0) {
      setForm((prev) => ({ ...prev, amount: "" }));
      return;
    }
    setForm((prev) => ({
      ...prev,
      amount: numberToMoney(wholeDollars),
    }));
  };

  const handleStreetPlaceSelected = useCallback((address) => {
    setForm((prev) => ({
      ...prev,
      line1: address.line1 || prev.line1,
      line2: address.line2 || "",
      city: address.city || "",
      state: resolveStateCode(address),
      postalCode: address.postalCode || "",
    }));
  }, []);

  const clearForm = () => {
    setForm(emptyCustomerForm(lockedRef ? { referenceId: lockedRef } : {}));
    setTransactionResult(null);
    setInvoiceResult(null);
    setSubmitError("");
    setLinkCopied(false);
    setShareMethod("email");
    setInvoiceEmail("");
    setInvoicePhone("");
    setActiveStep(0);
  };

  const buildCustomerPayload = useCallback(() => {
    const payload = {
      email: form.email.trim(),
      firstName: omitNamePlusSuffix(form.firstName),
      lastName: omitNamePlusSuffix(form.lastName),
      actor,
      address: {
        line1: form.line1.trim(),
        line2: form.line2.trim() || undefined,
        city: form.city.trim(),
        state: form.state.trim(),
        postalCode: form.postalCode.trim(),
      },
    };
    return payload;
  }, [actor, form]);

  const handleGenerateInvoiceLink = async ({ openInSameTab = false } = {}) => {
    if (!actor || !clientId) return;
    const amount = parseAmount(form.amount);
    if (amount === null) return;

    setSubmitting(true);
    setSubmitError("");
    setTransactionResult(null);
    setInvoiceResult(null);
    setLinkCopied(false);

    console.log("[payments] submitting with actor", actor);

    try {
      const customer = await createPaymentCustomer(
        clientId,
        buildCustomerPayload(),
        elevatedToken
      );
      if (!customer?.id) throw new Error("Failed to create customer.");

      const transaction = await createPaymentTransaction(
        clientId,
        {
          amount: dollarsToCents(amount),
          customerId: customer.id,
          description: form.description.trim(),
          referenceNumber:
            (lockedRef || form.referenceId.trim()) || undefined,
          actor,
        },
        elevatedToken
      );
      if (!transaction?.id) throw new Error("Failed to create transaction.");
      setTransactionResult(transaction);

      const invoice = await createPaymentInvoice(
        clientId,
        { transactionId: transaction.id, actor },
        elevatedToken
      );
      if (!invoice?.id) throw new Error("Failed to create invoice.");

      const link = resolveInvoiceLink(invoice, apiEnvironment);

      if (shareMethod === "email" || shareMethod === "sms") {
        const sendEmail = invoiceEmail.trim() || form.email.trim();
        const sendPhone = phoneDigits(invoicePhone);
        await sendPaymentInvoice(clientId, {
          invoiceId: invoice.id,
          method: shareMethod,
          // payment-pos accepts email on both methods (SMS curl includes it)
          email: sendEmail || undefined,
          phone: shareMethod === "sms" ? sendPhone : undefined,
        });
        setInvoiceResult({ ...invoice, url: link });
      } else if (openInSameTab || shareMethod === "manual") {
        if (!link) throw new Error("Invoice was created but no payment URL was returned.");
        // Leave submitting=true while navigating away
        window.location.assign(link);
        return;
      } else {
        setInvoiceResult({ ...invoice, url: link });
        if (link) {
          const copied = await copyTextToClipboard(link);
          setLinkCopied(copied);
        }
      }
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to generate invoice link."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinue = async () => {
    if (activeStep === 0 && canContinueStep0) {
      setActiveStep(1);
      return;
    }
    if (activeStep === 1 && canContinueStep1) {
      setInvoiceEmail(form.email.trim());
      // Pay invoice now → create invoice and open URL in this tab (skip step 3)
      if (shareMethod === "manual" && shareOnlineEnabled) {
        await handleGenerateInvoiceLink({ openInSameTab: true });
        return;
      }
      // Email / SMS → Create Payment Link step first
      setActiveStep(2);
    }
  };

  const handleBack = () => {
    setSubmitError("");
    setActiveStep((s) => Math.max(0, s - 1));
  };

  const invoiceLink = invoiceResult
    ? resolveInvoiceLink(invoiceResult, apiEnvironment)
    : null;

  const amountParsed = parseAmount(form.amount);
  const showNextSteps = activeStep >= 1 && !invoiceResult;
  const invoicePhoneDigits = phoneDigits(invoicePhone);
  const canSendInvoice =
    shareMethod === "manual" ||
    (shareMethod === "email" && Boolean(invoiceEmail.trim())) ||
    (shareMethod === "sms" && invoicePhoneDigits.length === 10);

  if (refLockReady && refLinkInvalid) {
    return (
      <InvalidSignedPaymentLinkPage
        partnerName={displayPartnerName}
        partnerEmail={
          String(sharedApp?.submittingUser?.email || accountOwnerEmail || "").trim()
        }
        partnerPhone={String(sharedApp?.submittingUser?.phone || "").trim()}
        detail={refVerifyError}
      />
    );
  }

  return (
    <Box
      className="pay-flow"
      sx={{
        bg: PAY.pageBg,
        borderRadius: PAY.radius,
        p: [3, 4],
        mx: [-2, 0],
      }}
    >
      {!invoiceResult ? (
        <Heading variant="h5" sx={{ mb: 3, letterSpacing: "-0.02em" }}>
          Payment to {displayPartnerName}
        </Heading>
      ) : null}

      {!invoiceResult ? <ProgressStepper activeStep={activeStep} /> : null}

      <Flex
        sx={{
          gap: 4,
          flexDirection: ["column", "column", showNextSteps ? "row" : "column"],
          alignItems: "flex-start",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            width: "100%",
            ...(invoiceResult
              ? {}
              : {
                  bg: "white",
                  borderRadius: PAY.radius,
                  border: "1px solid",
                  borderColor: "neutral.l3",
                  boxShadow: PAY.cardShadow,
                  p: [3, 4],
                }),
          }}
        >
          {activeStep === 0 && (
            <Box>
              {actorLoading ? (
                <Box sx={{ mb: 3, fontSize: 2, color: "neutral.d1" }}>
                  Resolving payment actor…
                </Box>
              ) : actorError ? (
                <Box sx={{ mb: 3, color: "danger.base", fontSize: 2 }}>
                  {actorError}
                </Box>
              ) : null}

              <Flex
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <Heading variant="h5" as="h3" sx={{ fontSize: [4, 5], m: 0 }}>
                  Start New Payment
                </Heading>
                <Button
                  variant="ghost"
                  type="button"
                  onClick={clearForm}
                  sx={{ borderRadius: PAY.radiusSm, fontWeight: 600 }}
                >
                  Clear form
                </Button>
              </Flex>

              <Heading variant="h6" as="h3" sx={{ mb: 2, fontSize: [2, 3] }}>
                Customer details
              </Heading>

              <Field id="pay-email" label="Email">
                <Box
                  as="input"
                  id="pay-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField("email", e.target.value)}
                  sx={fieldSx}
                />
              </Field>
              <Flex sx={{ gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ flex: "1 1 200px" }}>
                  <Field id="pay-first" label="First name">
                    <Box
                      as="input"
                      id="pay-first"
                      value={form.firstName}
                      onChange={(e) => updateField("firstName", e.target.value)}
                      sx={fieldSx}
                    />
                  </Field>
                </Box>
                <Box sx={{ flex: "1 1 200px" }}>
                  <Field id="pay-last" label="Last name">
                    <Box
                      as="input"
                      id="pay-last"
                      value={form.lastName}
                      onChange={(e) => updateField("lastName", e.target.value)}
                      sx={fieldSx}
                    />
                  </Field>
                </Box>
              </Flex>

              <Heading variant="h6" as="h3" sx={{ mb: 2 }}>
                Project address
              </Heading>

              <Box sx={{ mb: 3 }}>
                <StreetAddressAutocomplete
                  label="Street address"
                  value={form.line1}
                  onChange={(v) => updateField("line1", v)}
                  onPlaceSelected={handleStreetPlaceSelected}
                />
              </Box>
              <Field id="pay-line2" label="Address 2 (optional)">
                <Box
                  as="input"
                  id="pay-line2"
                  value={form.line2}
                  onChange={(e) => updateField("line2", e.target.value)}
                  sx={fieldSx}
                />
              </Field>
              <Flex sx={{ gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ flex: "2 1 160px" }}>
                  <Field id="pay-city" label="City">
                    <Box
                      as="input"
                      id="pay-city"
                      value={form.city}
                      onChange={(e) => updateField("city", e.target.value)}
                      sx={fieldSx}
                    />
                  </Field>
                </Box>
                <Box sx={{ flex: "1 1 100px" }}>
                  <Field id="pay-state" label="State">
                    <Box
                      as="select"
                      id="pay-state"
                      value={form.state}
                      onChange={(e) => updateField("state", e.target.value)}
                      sx={fieldSx}
                    >
                      <option value="">Select</option>
                      {US_PROJECT_ADDRESS_STATES.map((s) => (
                        <option key={s.code} value={s.code}>
                          {s.name}
                        </option>
                      ))}
                    </Box>
                  </Field>
                </Box>
                <Box sx={{ flex: "1 1 100px" }}>
                  <Field id="pay-zip" label="ZIP">
                    <Box
                      as="input"
                      id="pay-zip"
                      value={form.postalCode}
                      onChange={(e) => updateField("postalCode", e.target.value)}
                      sx={fieldSx}
                    />
                  </Field>
                </Box>
              </Flex>

              <Box
                sx={{
                  borderTop: "1px solid",
                  borderColor: "neutral.l3",
                  pt: 3,
                }}
              >
                {refVerifyError && !refLinkInvalid ? (
                  <Box sx={{ mb: 2, color: "danger.base", fontSize: 2 }}>
                    {refVerifyError}
                  </Box>
                ) : null}
                <Field
                  id="pay-ref"
                  label="Reference ID"
                >
                  <Box
                    as="input"
                    id="pay-ref"
                    value={lockedRef || form.referenceId}
                    onChange={(e) => {
                      if (lockedRef) return;
                      updateField("referenceId", e.target.value);
                    }}
                    readOnly={Boolean(lockedRef)}
                    disabled={Boolean(lockedRef) || !refLockReady}
                    sx={{
                      ...fieldSx,
                      ...(lockedRef
                        ? {
                            bg: "neutral.l4",
                            color: "neutral.d2",
                            cursor: "not-allowed",
                            "&:hover": { borderColor: "neutral.l3" },
                            "&:focus": {
                              outline: "none",
                              borderColor: "neutral.l3",
                              boxShadow: "none",
                              bg: "neutral.l4",
                            },
                          }
                        : null),
                    }}
                  />
                </Field>
              </Box>

              <Flex sx={{ justifyContent: "flex-end", mt: 1 }}>
                <Button
                  type="button"
                  onClick={handleContinue}
                  disabled={!canContinueStep0 || actorLoading || !refLockReady}
                  sx={primaryButtonSx}
                >
                  Continue
                </Button>
              </Flex>
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <BackButton onClick={handleBack} />
              <Heading variant="h5" as="h3" sx={{ mb: 3, fontSize: [3, 4] }}>
                Payment Details
              </Heading>
              <Field id="pay-amount" label="Amount (USD)">
                <Box
                  as="input"
                  id="pay-amount"
                  value={form.amount}
                  onChange={(e) => updateAmount(e.target.value)}
                  onBlur={() => {
                    const n = parseAmount(form.amount);
                    if (n != null) {
                      setForm((prev) => ({
                        ...prev,
                        amount: numberToMoney2(n),
                      }));
                    }
                  }}
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="$0"
                  sx={fieldSx}
                />
              </Field>
              <Field id="pay-desc" label="Description">
                <Box
                  as="input"
                  id="pay-desc"
                  value={form.description}
                  onChange={(e) => updateField("description", e.target.value)}
                  sx={fieldSx}
                />
              </Field>

              <Box sx={{ mb: 3, mt: 3 }}>
                <Box sx={{ ...labelSx, mb: 2 }}>How should we share the invoice?</Box>
                <Flex sx={{ gap: 4, flexWrap: "wrap", alignItems: "center" }}>
                  {shareMethodFlags.email ? (
                    <Box
                      as="label"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 2,
                        cursor: "pointer",
                        fontSize: 2,
                      }}
                    >
                      <Box
                        as="input"
                        type="radio"
                        name="shareMethodStep1"
                        checked={shareMethod === "email"}
                        onChange={() => {
                          setShareMethod("email");
                          if (!invoiceEmail.trim()) {
                            setInvoiceEmail(form.email.trim());
                          }
                        }}
                        sx={{ accentColor: PAY.accent, width: 18, height: 18 }}
                      />
                      Email payment link
                    </Box>
                  ) : null}
                  {shareMethodFlags.sms ? (
                    <Box
                      as="label"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 2,
                        cursor: "pointer",
                        fontSize: 2,
                      }}
                    >
                      <Box
                        as="input"
                        type="radio"
                        name="shareMethodStep1"
                        checked={shareMethod === "sms"}
                        onChange={() => setShareMethod("sms")}
                        sx={{ accentColor: PAY.accent, width: 18, height: 18 }}
                      />
                      Send via SMS
                    </Box>
                  ) : null}
                  {shareMethodFlags.online ? (
                    <Box
                      as="label"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 2,
                        cursor: "pointer",
                        fontSize: 2,
                      }}
                    >
                      <Box
                        as="input"
                        type="radio"
                        name="shareMethodStep1"
                        checked={shareMethod === "manual"}
                        onChange={() => setShareMethod("manual")}
                        sx={{ accentColor: PAY.accent, width: 18, height: 18 }}
                      />
                      Pay invoice now
                    </Box>
                  ) : null}
                </Flex>
              </Box>

              {submitError && shareMethod === "manual" ? (
                <Box sx={{ mb: 2, color: "danger.base" }}>{submitError}</Box>
              ) : null}

              <Flex sx={{ justifyContent: "flex-end", mt: 1 }}>
                <Button
                  type="button"
                  onClick={handleContinue}
                  disabled={
                    !canContinueStep1 ||
                    submitting ||
                    (shareMethod === "manual" && !actor)
                  }
                  sx={primaryButtonSx}
                >
                  {submitting && shareMethod === "manual"
                    ? "Opening invoice…"
                    : shareMethod === "manual"
                      ? "Pay invoice now"
                      : "Continue"}
                </Button>
              </Flex>
            </Box>
          )}

          {activeStep === 2 && !invoiceResult && (
            <Box>
              <BackButton onClick={handleBack} disabled={submitting} />
              <Heading variant="h5" as="h3" sx={{ mb: 3, fontSize: [3, 4] }}>
                Create Payment Link
              </Heading>

              {shareMethodFlags.email && shareMethod === "email" ? (
                <Box sx={{ mb: 3, maxWidth: 420 }}>
                  <Field id="pay-invoice-email" label="Send invoice to">
                    <Box
                      as="input"
                      id="pay-invoice-email"
                      type="email"
                      value={invoiceEmail}
                      onChange={(e) => setInvoiceEmail(e.target.value)}
                      placeholder="customer@email.com"
                      sx={fieldSx}
                    />
                  </Field>
                </Box>
              ) : null}

              {shareMethodFlags.sms && shareMethod === "sms" ? (
                <Box sx={{ mb: 3, maxWidth: 420 }}>
                  <Field
                    id="pay-invoice-phone"
                    label="Mobile phone"
                    description="10-digit US number"
                  >
                    <Box
                      as="input"
                      id="pay-invoice-phone"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel-national"
                      value={invoicePhone}
                      onChange={(e) =>
                        setInvoicePhone(formatPhoneInput(e.target.value))
                      }
                      placeholder="(555) 555-5555"
                      sx={fieldSx}
                    />
                  </Field>
                </Box>
              ) : null}

              {shareMethodFlags.sms && shareMethod === "sms" ? (
                <Flex
                  sx={{
                    mb: 3,
                    alignItems: "flex-start",
                    gap: 2,
                    bg: "rgba(245, 158, 11, 0.1)",
                    borderLeft: "4px solid",
                    borderLeftColor: "warning.base",
                    borderRadius: PAY.radiusSm,
                    px: 3,
                    py: 3,
                    color: "neutral.d1",
                    fontSize: 1,
                    lineHeight: 1.45,
                  }}
                >
                  <Icon
                    variant="color-warning"
                    size="sm"
                    sx={{ flexShrink: 0, mt: "2px" }}
                  />
                  <Box>
                    By providing your phone number, you agree to receive
                    transactional and account-related text messages from{" "}
                    <Box as="strong" sx={{ fontWeight: 700 }}>
                      GoodLeap
                    </Box>{" "}
                    on behalf of{" "}
                    <Box as="strong" sx={{ fontWeight: 700 }}>
                      {displayPartnerName || "this company"}
                    </Box>
                    . Consent is not a condition of approval or purchase. Message
                    frequency varies. Msg & data rates may apply. Reply STOP
                    to opt-out.
                  </Box>
                </Flex>
              ) : null}

              <Flex
                sx={{
                  mb: 3,
                  alignItems: "flex-start",
                  gap: 2,
                  bg: "rgba(0, 149, 255, 0.08)",
                  borderLeft: "4px solid",
                  borderLeftColor: "info.base",
                  borderRadius: PAY.radiusSm,
                  px: 3,
                  py: 3,
                  color: "neutral.d1",
                  fontSize: 2,
                }}
              >
                <Icon
                  variant="color-info"
                  size="sm"
                  sx={{ flexShrink: 0, mt: "2px" }}
                />
                <Box>
                  {shareMethod === "sms" ? (
                    <>
                      We&apos;ll text the invoice link to{" "}
                      <Box as="strong" sx={{ fontWeight: 700, color: "neutral.d1" }}>
                        {invoicePhoneDigits.length === 10
                          ? formatPhoneDisplay(invoicePhoneDigits)
                          : "the customer"}
                      </Box>
                      .
                    </>
                  ) : (
                    <>
                      We&apos;ll email the invoice to{" "}
                      <Box as="strong" sx={{ fontWeight: 700, color: "neutral.d1" }}>
                        {invoiceEmail.trim() || "the customer"}
                      </Box>{" "}
                      with a Pay invoice link.
                    </>
                  )}
                </Box>
              </Flex>

              {submitError ? (
                <Box sx={{ mb: 2, color: "danger.base" }}>{submitError}</Box>
              ) : null}
              <Flex sx={{ justifyContent: "flex-end", mt: 1 }}>
                <Button
                  type="button"
                  onClick={() => handleGenerateInvoiceLink()}
                  disabled={submitting || !actor || !canSendInvoice}
                  sx={primaryButtonSx}
                >
                  {submitting
                    ? "Sending…"
                    : shareMethod === "sms"
                      ? "Send Invoice SMS"
                      : "Send Invoice Email"}
                </Button>
              </Flex>
            </Box>
          )}

          {invoiceResult && invoiceLink && (
            <Box sx={{ maxWidth: 720, mx: "auto", width: "100%" }}>
              <Flex
                sx={{
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 3,
                  mb: 4,
                  textAlign: "center",
                }}
              >
                <Icon
                  variant="check-circle"
                  size="md"
                  sx={{ color: "#15803d", flexShrink: 0 }}
                />
                <Heading variant="h5" as="h3" sx={{ fontSize: [3, 4], m: 0 }}>
                  {shareMethod === "email"
                    ? "Done! Email sent!"
                    : shareMethod === "sms"
                      ? "Done! SMS sent!"
                      : linkCopied
                        ? "Invoice link created and copied!"
                        : "Invoice link created!"}
                </Heading>
              </Flex>

              <Box sx={cardSx}>
                <Flex sx={{ alignItems: "center", gap: 2, mb: 2 }}>
                  <Icon variant="attachment" size="sm" sx={{ color: "neutral.d2" }} />
                  <Box sx={{ fontWeight: 600, fontSize: 2 }}>Invoice link</Box>
                </Flex>
                <Flex sx={{ gap: 2, flexWrap: "wrap", alignItems: "stretch" }}>
                  <Box
                    as="input"
                    readOnly
                    value={invoiceLink}
                    onClick={(e) => e.target.select?.()}
                    sx={{ ...fieldSx, flex: "1 1 240px", bg: "neutral.l4" }}
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={async () => {
                      const copied = await copyTextToClipboard(invoiceLink);
                      setLinkCopied(copied);
                    }}
                    sx={{
                      ...secondaryButtonSx,
                      flexShrink: 0,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 2,
                      color: PAY.accent,
                      borderColor: PAY.accent,
                    }}
                  >
                    <Icon variant="clipboard" size="sm" />
                    {linkCopied ? "Copied" : "Copy"}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() =>
                      window.open(invoiceLink, "_blank", "noopener,noreferrer")
                    }
                    sx={{
                      ...secondaryButtonSx,
                      flexShrink: 0,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 2,
                      color: PAY.accent,
                      borderColor: PAY.accent,
                    }}
                  >
                    <Icon variant="open-new-tab" size="sm" />
                    Open
                  </Button>
                </Flex>
              </Box>

              <Box sx={{ ...cardSx, mb: 4 }}>
                <Flex sx={{ flexDirection: "column", gap: 2, mb: 3 }}>
                  <SummaryDetailRow icon="user">
                    {`${omitNamePlusSuffix(form.firstName)} ${omitNamePlusSuffix(form.lastName)}`.trim() || "—"}
                  </SummaryDetailRow>
                  <SummaryDetailRow icon="envelope">
                    <Flex sx={{ alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                      <Box as="span">
                        {invoiceEmail.trim() || form.email.trim() || "—"}
                      </Box>
                      {shareMethod === "email" ? <SentCheckmark /> : null}
                    </Flex>
                  </SummaryDetailRow>
                  <SummaryDetailRow icon="phone">
                    <Flex sx={{ alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                      <Box as="span">
                        {invoicePhoneDigits.length === 10
                          ? formatPhoneDisplay(invoicePhoneDigits)
                          : invoicePhone.trim() || "—"}
                      </Box>
                      {shareMethod === "sms" ? <SentCheckmark /> : null}
                    </Flex>
                  </SummaryDetailRow>
                  {hasAddressInput ? (
                    <SummaryDetailRow icon="home">
                      {formatAddress(form)}
                    </SummaryDetailRow>
                  ) : null}
                  {(form.referenceId.trim() ||
                    transactionResult?.referenceNumber ||
                    invoiceResult?.referenceNumber) && (
                    <SummaryDetailRow icon="clipboard">
                      {form.referenceId.trim() ||
                        transactionResult?.referenceNumber ||
                        invoiceResult?.referenceNumber}
                    </SummaryDetailRow>
                  )}
                </Flex>

                <Box
                  sx={{
                    bg: PAY.accentSoft,
                    borderRadius: PAY.radiusSm,
                    p: 3,
                  }}
                >
                  <Box sx={{ fontWeight: 600, fontSize: 2, mb: 2 }}>
                    Order summary
                  </Box>
                  <Flex
                    sx={{
                      alignItems: "center",
                      gap: 2,
                      mb: 2,
                      color: "neutral.d1",
                      fontSize: 2,
                    }}
                  >
                    <Icon variant="payment" size="sm" />
                    <Box>Custom charge</Box>
                  </Flex>
                  <Flex
                    sx={{
                      justifyContent: "space-between",
                      gap: 2,
                      mb: 2,
                      color: "neutral.d1",
                      fontSize: 2,
                    }}
                  >
                    <Box sx={{ wordBreak: "break-word" }}>
                      {form.description.trim() || "—"}
                    </Box>
                    <Box sx={{ flexShrink: 0 }}>
                      {amountParsed != null
                        ? numberToMoney2(amountParsed)
                        : "—"}
                    </Box>
                  </Flex>
                  <Box
                    sx={{
                      borderTop: "1px solid",
                      borderColor: "neutral.l3",
                      pt: 2,
                    }}
                  />
                  <Flex
                    sx={{
                      justifyContent: "space-between",
                      fontWeight: 700,
                      mt: 2,
                      fontSize: 2,
                    }}
                  >
                    <span>Invoice total</span>
                    <span>
                      {amountParsed != null
                        ? numberToMoney2(amountParsed)
                        : "—"}
                    </span>
                  </Flex>
                </Box>
              </Box>

              <Flex sx={{ justifyContent: "flex-end" }}>
                <Button
                  type="button"
                  onClick={clearForm}
                  sx={{ ...primaryButtonSx, minWidth: 140 }}
                >
                  Done
                </Button>
              </Flex>
            </Box>
          )}
        </Box>

        {showNextSteps && (
          <Box
            sx={{
              width: ["100%", "100%", 320],
              flexShrink: 0,
              border: "1px solid",
              borderColor: "neutral.l3",
              borderRadius: PAY.radius,
              p: 3,
              position: ["static", "static", "sticky"],
              top: 24,
              bg: "white",
              boxShadow: PAY.cardShadow,
            }}
          >
            <Heading variant="h6" as="h3" sx={{ mb: 3, fontWeight: 600 }}>
              Next steps
            </Heading>

            <NextStepBlock
              status="complete"
              title="Start new payment"
              onEdit={() => setActiveStep(0)}
            >
              <Flex sx={{ flexDirection: "column", gap: 2 }}>
                <SummaryDetailRow icon="user">
                  {`${omitNamePlusSuffix(form.firstName)} ${omitNamePlusSuffix(form.lastName)}`.trim() || "—"}
                </SummaryDetailRow>
                <SummaryDetailRow icon="envelope">
                  {form.email.trim() || "—"}
                </SummaryDetailRow>
                {hasAddressInput ? (
                  <SummaryDetailRow icon="home">
                    {formatAddress(form)}
                  </SummaryDetailRow>
                ) : null}
                {form.referenceId.trim() ? (
                  <SummaryDetailRow icon="clipboard">
                    {form.referenceId.trim()}
                  </SummaryDetailRow>
                ) : null}
              </Flex>
            </NextStepBlock>

            <NextStepBlock
              status={
                amountParsed != null && form.description.trim() && activeStep > 1
                  ? "complete"
                  : activeStep === 1
                    ? "active"
                    : "pending"
              }
              title="Payment Details"
              onEdit={
                amountParsed != null && form.description.trim() && activeStep > 1
                  ? () => setActiveStep(1)
                  : undefined
              }
            >
              {amountParsed != null && form.description.trim() ? (
                <Box sx={{ fontSize: 2 }}>
                  <Flex sx={{ alignItems: "center", gap: 2, mb: 2, color: "neutral.d1" }}>
                    <Icon variant="payment" size="sm" />
                    <Box>Custom charge</Box>
                  </Flex>
                  <Flex
                    sx={{
                      justifyContent: "space-between",
                      gap: 2,
                      mb: 2,
                      pl: "28px",
                      color: "neutral.d1",
                    }}
                  >
                    <Box sx={{ wordBreak: "break-word" }}>
                      {form.description.trim()}
                    </Box>
                    <Box sx={{ flexShrink: 0 }}>
                      {numberToMoney2(amountParsed)}
                    </Box>
                  </Flex>
                  <Box
                    sx={{
                      borderTop: "1px solid",
                      borderColor: "neutral.l3",
                      pt: 2,
                    }}
                  />
                  <Flex
                    sx={{
                      justifyContent: "space-between",
                      fontWeight: 700,
                      mt: 2,
                    }}
                  >
                    <span>Total</span>
                    <span>{numberToMoney2(amountParsed)}</span>
                  </Flex>
                </Box>
              ) : null}
            </NextStepBlock>

            <NextStepBlock
              status={activeStep === 2 ? "active" : "pending"}
              title="Create Payment Link"
            />
          </Box>
        )}
      </Flex>
    </Box>
  );
}

/**
 * Shown when a signed ?ref=&exp=&sig= link fails verification (tampered/expired).
 * Matches PaymentsNotEnabled / ErrorPage layout; contact the partner.
 */
function InvalidSignedPaymentLinkPage({
  partnerName,
  partnerEmail,
  partnerPhone,
  detail,
}) {
  const name = String(partnerName || "your contractor").trim();
  const email = String(partnerEmail || "").trim();
  const phoneRaw = String(partnerPhone || "").trim();
  const phoneDigitsOnly = phoneDigits(phoneRaw);
  const phoneLabel =
    phoneDigitsOnly.length === 10
      ? formatPhoneDisplay(phoneDigitsOnly)
      : phoneRaw;
  const supportPhone = CUSTOMER_SUPPORT_PHONE || "";

  return (
    <Box
      sx={{
        pt: [2, 4],
        pb: 4,
        "@media screen and (max-width: 80em)": {
          pt: 2,
        },
      }}
    >
      <Box sx={{ width: "100%", mx: "auto", maxWidth: 560 }}>
        <Card variant="float" sx={{ px: 4 }}>
          <Heading
            variant="h4"
            as="h2"
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              fontSize: ["18px", "20px"],
              lineHeight: 1.3,
            }}
          >
            <Icon
              variant="color-warning"
              sx={{ mr: 3, flexShrink: 0 }}
              size="md"
            />
            This payment link is not valid.
          </Heading>
          <Box
            sx={{
              mt: 1,
              pb: 3,
              mb: 3,
              borderBottom: "1px solid",
              borderColor: "neutral.l3",
            }}
          >
            The link may have been changed or expired. Please contact{" "}
            <Box as="strong" sx={{ fontWeight: 700 }}>
              {name}
            </Box>{" "}
            for a new payment link.
            {detail ? (
              <Box sx={{ mt: 2, fontSize: 1, color: "neutral.d2" }}>
                {detail}
              </Box>
            ) : null}
          </Box>
          <section>
            <Heading variant="h5" as="div">
              What should I do?
            </Heading>
            <Box
              as="ul"
              sx={{ p: 0, mt: "12px", listStyleType: "none", ml: -2 }}
            >
              {phoneLabel ? (
                <Box
                  as="li"
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "32px 1fr",
                    gap: "12px",
                    alignItems: "flex-start",
                    mb: "12px",
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      bg: "primary.l4",
                      borderRadius: "full",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon variant="phone" color="primary.d1" size="sm" />
                  </Box>
                  <Box>
                    Call {name} at&nbsp;
                    <a
                      href={`tel:${phoneDigitsOnly || phoneRaw.replace(/\D/g, "")}`}
                    >
                      {phoneLabel}
                    </a>
                  </Box>
                </Box>
              ) : null}
              {email ? (
                <Box
                  as="li"
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "32px 1fr",
                    gap: "12px",
                    alignItems: "flex-start",
                    mb: "12px",
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      bg: "primary.l4",
                      borderRadius: "full",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon variant="envelope" color="primary.d1" size="sm" />
                  </Box>
                  <Box>
                    Email {name} at&nbsp;
                    <a href={`mailto:${email}`}>{email}</a>
                  </Box>
                </Box>
              ) : null}
              {!phoneLabel && !email && supportPhone ? (
                <Box
                  as="li"
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "32px 1fr",
                    gap: "12px",
                    alignItems: "flex-start",
                    mb: "12px",
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      bg: "primary.l4",
                      borderRadius: "full",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon variant="phone" color="primary.d1" size="sm" />
                  </Box>
                  <Box>
                    Call GoodLeap Customer Support at&nbsp;
                    <a href={`tel:${supportPhone.replace(/\D/g, "")}`}>
                      {supportPhone}
                    </a>
                  </Box>
                </Box>
              ) : null}
            </Box>
          </section>
        </Card>
      </Box>
    </Box>
  );
}
